import { element as el } from '../dom.js';
import { icon } from '../icons.js';
import { CONTAINER_RADIUS, LOOT_REACH, TRADE_REACH, carryRoom, formatCoins, type ItemDefinition } from '../../../shared/game/supply-types.js';
import type { GameContent, Point } from '../../../shared/game/world-types.js';
import { GEOMETRY_EPSILON, surfaceDistance } from '../../../shared/navigation/geometry.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { itemGlyph, itemTier, itemTooltip, slotNode } from './item-slot.js';

/** Which window is open: a chest or spoils on the ground, or a merchant's stall. */
export type LootTarget = { kind: 'container' | 'merchant'; id: string };
export interface LootActions {
  take(heroId: string, containerId: string, pick: { gold?: boolean; itemId?: string }): void;
  buy(heroId: string, merchantId: string, itemId: string): void;
}
interface Entry { gold?: true; itemId?: string; count: number; price?: number; reason: string }
interface LootModel { target: LootTarget; name: string; hero: string; near: boolean; trade: boolean; entries: Entry[] }
/** Whether a hero stands close enough to take from a chest (or trade at a stall) at `spot`. */
export function inReach(hero: Point & { radius: number }, spot: Point, kind: LootTarget['kind']) {
  return surfaceDistance(hero, { x: spot.x, z: spot.z, radius: CONTAINER_RADIUS }) <= (kind === 'container' ? LOOT_REACH : TRADE_REACH) + GEOMETRY_EPSILON;
}
const make = <K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = '') => {
  const node = document.createElement(tag); if (className) node.className = className; if (text) node.textContent = text; return node;
};

/**
 * The window for whatever the party is standing at. Entries are icon slots: click one to take (or buy) it, or drag it into the pack.
 * The server decides everything; this only mirrors reach, space and gold to explain why a slot is blocked.
 */
export class LootPanel {
  private signature = '';
  constructor(private actions: LootActions) {}
  reset() { this.signature = ''; }

  /** Redraws the window and returns false once its target no longer exists, so the caller can close it. */
  update(snapshot: WorldSnapshot, content: GameContent, heroId: string | undefined, target: LootTarget): boolean {
    const catalog = new Map((content.items ?? []).map(item => [item.id, item])), hero = snapshot.heroes.find(candidate => candidate.id === heroId);
    const strength = content.heroes.find(definition => definition.id === heroId)?.profile.attributes.strength ?? 0;
    const container = target.kind === 'container' ? snapshot.containers.find(candidate => candidate.id === target.id) : undefined;
    const merchant = target.kind === 'merchant' ? content.merchants?.find(candidate => candidate.id === target.id) : undefined;
    const stock = merchant && snapshot.merchants.find(candidate => candidate.id === merchant.id);
    if (!container && !(merchant && stock)) return false;
    const near = !!hero && !hero.health.dead && inReach(hero, container ?? merchant!, target.kind);
    const raw: Omit<Entry, 'reason'>[] = container
      ? [...container.gold > 0 ? [{ gold: true as const, count: container.gold }] : [], ...Object.entries(container.items).filter(([, count]) => count > 0).map(([itemId, count]) => ({ itemId, count }))]
      : merchant!.stock.map(row => ({ itemId: row.itemId, count: stock!.stock[row.itemId] ?? 0, price: row.price }));
    const reason = (entry: Omit<Entry, 'reason'>) => {
      if (!hero || hero.health.dead) return 'Select a living hero.';
      if (snapshot.inCombat) return merchant ? 'Merchants will not trade during a fight.' : 'Loot has to wait until the fight is over.';
      if (!near) return 'Move closer first.';
      if (entry.gold) return '';
      if (entry.price !== undefined) return !entry.count ? 'Sold out.' : hero.inventory.gold < entry.price ? 'Not enough gold.' : carryRoom(hero.inventory.items, strength, catalog, entry.itemId!) < 1 ? 'The pack is full.' : '';
      return carryRoom(hero.inventory.items, strength, catalog, entry.itemId!) < 1 ? 'The pack is full.' : '';
    };
    const model: LootModel = { target, name: container?.name ?? merchant!.name, hero: hero?.name ?? '', near, trade: !!merchant, entries: raw.map(entry => ({ ...entry, reason: reason(entry) })) };
    // Gold and carried counts change what is affordable; they are already folded into each entry's reason.
    const signature = JSON.stringify(model);
    if (signature === this.signature || document.body.classList.contains('dragging-item')) return true;
    this.signature = signature; this.render(model, catalog, hero?.id);
    return true;
  }

  private render(model: LootModel, catalog: Map<string, ItemDefinition>, heroId: string | undefined) {
    el('loot-title').textContent = model.name;
    const body = el('loot-body'); body.replaceChildren();
    if (!model.near) body.append(make('p', 'supply-empty', model.trade ? 'Too far to trade.' : 'Too far to reach.'));
    const grid = make('div', 'slot-grid loot-grid');
    for (const entry of model.entries) {
      const item = entry.itemId ? catalog.get(entry.itemId) : undefined;
      if (!entry.gold && !item) continue;
      const act = () => {
        if (!heroId || entry.reason) return;
        if (model.trade) this.actions.buy(heroId, model.target.id, entry.itemId!); else this.actions.take(heroId, model.target.id, entry.gold ? { gold: true } : { itemId: entry.itemId });
      };
      const verb = entry.reason;
      const slot = slotNode(entry.gold
        ? { glyph: 'gold', tier: 'gold', count: entry.count, label: `${entry.count} gold`, tooltip: `${entry.count} gold pieces. ${verb}`, disabled: !!entry.reason, drag: entry.reason || model.trade ? undefined : { from: 'loot', containerId: model.target.id, gold: true } }
        : { glyph: itemGlyph(item!), tier: itemTier(item!), count: model.trade ? undefined : entry.count, label: item!.name, price: entry.price, disabled: !!entry.reason,
          tooltip: `${itemTooltip(item!, model.trade ? undefined : entry.count)}${model.trade ? ` ${formatCoins(entry.price!)}, ${entry.count} left.` : ''} ${verb}`,
          drag: entry.reason ? undefined : model.trade ? { from: 'trade', merchantId: model.target.id, itemId: entry.itemId! } : { from: 'loot', containerId: model.target.id, itemId: entry.itemId } });
      slot.addEventListener('click', act);
      slot.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); act(); } });
      grid.append(slot);
    }
    body.append(grid);
    if (!model.trade && model.near && model.entries.length > 1) {
      const all = make('button', 'secondary take-all'); all.type = 'button'; all.append(icon('grab'), make('span', '', 'Take all'));
      all.disabled = model.entries.every(entry => !!entry.reason);
      all.addEventListener('click', () => { if (heroId) this.actions.take(heroId, model.target.id, {}); }); body.append(all);
    }
  }
}
